import React, { useState, useEffect } from "react";
import ReportTemplateBuilder from "../components/ReportTemplateBuilder";
import { useSimpleDataLoader } from "../hooks/useSimpleDataLoader";
import { exportToCSV, exportToExcel, exportToJSON, exportDetailedCSV } from "../utils/reportExport";

const ReportTemplatesPage = () => {
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`
  });
  const { allData, loading, error, refreshData, fieldMetadata } = useSimpleDataLoader();
  console.log('ReportTemplatesPage - данные из хука:', {
    allDataLength: allData?.length,
    loading,
    error,
    fieldMetadataKeys: Object.keys(fieldMetadata || {}).length,
    fieldMetadataPreview: fieldMetadata
  });

  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  // Загружаем сохраненные шаблоны с сервера
  useEffect(() => {
    loadTemplates();
  }, []);

  // Загрузка шаблонов с сервера
  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const username = localStorage.getItem('username'); // Получаем текущего пользователя
      const response = await fetch(`/api/report-templates?username=${username || ''}`);
      const result = await response.json();

      if (result.success) {
        setSavedTemplates(result.data);
      } else {
        console.error('Ошибка загрузки шаблонов:', result.error);
      }
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Создание нового шаблона
  const createNewTemplate = () => {
    setSelectedTemplate(null);
    setIsBuilderOpen(true);
  };

  // Редактирование шаблона
  const editTemplate = (template) => {
    setSelectedTemplate(template);
    setIsBuilderOpen(true);
  };

  // Сохранение шаблона
  const saveTemplate = async (template) => {
    try {
      const username = localStorage.getItem('username');
      const templateData = {
        name: template.name,
        description: template.description,
        config: {
          dateRange: template.dateRange,
          filters: template.filters,
          grouping: template.grouping,
          aggregations: template.aggregations,
          sorting: template.sorting
        },
        createdBy: username,
        isShared: template.isShared || false
      };

      let response;
      if (selectedTemplate) {
        // Обновление существующего шаблона
        response = await fetch(`/api/report-templates/${selectedTemplate.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(templateData)
        });
      } else {
        // Создание нового шаблона
        response = await fetch('/api/report-templates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(templateData)
        });
      }

      const result = await response.json();

      if (result.success) {
        await loadTemplates(); // Перезагружаем список шаблонов
        setIsBuilderOpen(false);
        setSelectedTemplate(null);
      } else {
        alert('Ошибка сохранения шаблона: ' + result.error);
      }
    } catch (error) {
      console.error('Ошибка сохранения шаблона:', error);
      alert('Ошибка сохранения шаблона');
    }
  };

  // Удаление шаблона
  const deleteTemplate = async (templateId) => {
    if (!confirm('Вы уверены, что хотите удалить этот шаблон?')) {
      return;
    }

    try {
      const response = await fetch(`/api/report-templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (result.success) {
        await loadTemplates(); // Перезагружаем список шаблонов
      } else {
        alert('Ошибка удаления шаблона: ' + result.error);
      }
    } catch (error) {
      console.error('Ошибка удаления шаблона:', error);
      alert('Ошибка удаления шаблона');
    }
  };

  // Применение шаблона
  const applyTemplate = async (template) => {
    try {
      console.log('Применяется шаблон:', template.name);
      console.log('Конфигурация шаблона:', template);

      // Проверяем есть ли период в шаблоне
      const templateDateRange = template.dateRange;
      let dataToUse = allData;

      // Если в шаблоне указан период, загружаем данные для этого периода
      if (templateDateRange && (templateDateRange.from || templateDateRange.to)) {
        console.log('Загружаем данные для периода из шаблона:', templateDateRange);

        // Показываем индикатор загрузки
        const loadingMessage = `Загружаем данные за период ${templateDateRange.from || 'начало'} - ${templateDateRange.to || 'текущая дата'}...`;
        const shouldProceed = confirm(`${loadingMessage}\n\nПродолжить?`);

        if (!shouldProceed) return;

        // Загружаем данные для указанного периода
        await new Promise((resolve, reject) => {
          // Используем refreshData с периодом из шаблона
          refreshData(templateDateRange);

          // Ждем завершения загрузки
          const checkLoading = setInterval(() => {
            if (!loading) {
              clearInterval(checkLoading);
              if (error) {
                reject(new Error(error));
              } else {
                resolve();
              }
            }
          }, 100);

          // Таймаут на случай зависания
          setTimeout(() => {
            clearInterval(checkLoading);
            reject(new Error('Превышено время ожидания загрузки данных'));
          }, 30000);
        });

        dataToUse = allData;
        console.log(`Данные загружены для периода. Всего записей: ${dataToUse.length}`);
      }

      // Проверяем есть ли данные для обработки
      if (!dataToUse || dataToUse.length === 0) {
        alert('Нет данных для применения шаблона за указанный период. Попробуйте изменить период или проверьте наличие данных.');
        return;
      }

      // Импортируем утилиты для работы с шаблонами
      const { applyReportTemplate } = await import('../utils/reportTemplateUtils.js');

      // Применяем шаблон к данным
      const reportResult = applyReportTemplate(dataToUse, template);
      console.log('Результат применения шаблона:', reportResult);

      // Показываем результат пользователю
      const resultText = formatReportResult(template, reportResult);

      // Создаем модальное окно с результатами
      showReportResultModal(template, reportResult, resultText);

    } catch (error) {
      console.error('Ошибка применения шаблона:', error);
      alert('Ошибка при применении шаблона: ' + error.message);
    }
  };

  // Форматирование результатов отчета для отображения
  const formatReportResult = (template, result) => {
    const { processedData, summary } = result;

    let text = `Отчет "${template.name}"\n`;

    // Добавляем информацию о периоде
    if (template.dateRange && (template.dateRange.from || template.dateRange.to)) {
      text += `Период: ${template.dateRange.from || 'начало'} — ${template.dateRange.to || 'текущая дата'}\n`;
    }

    text += `Всего записей: ${summary.totalRecords}\n`;
    text += `Групп: ${summary.totalGroups}\n\n`;

    processedData.forEach((group, index) => {
      text += `${index + 1}. ${group.groupKey}: ${group.count} записей\n`;

      // Показываем агрегированные данные если есть
      Object.entries(group).forEach(([key, value]) => {
        if (!['groupKey', 'count', 'items'].includes(key) && typeof value === 'number') {
          text += `   ${key}: ${value}\n`;
        }
      });
    });

    return text;
  };

  // Показ модального окна с результатами
  const showReportResultModal = (template, result, resultText) => {
    // Сохраняем результат для экспорта
    setReportResult({ template, result, resultText });
    setShowExportModal(true);
  };

  // Экспорт отчета
  const handleExport = (format) => {
    if (!reportResult) return;

    const { template, result } = reportResult;
    const filename = `${template.name}_${new Date().toISOString().split('T')[0]}`;

    switch (format) {
      case 'csv':
        exportToCSV(result.processedData, template, filename);
        break;
      case 'excel':
        exportToExcel(result.processedData, template, filename);
        break;
      case 'json':
        exportToJSON(result.processedData, template, filename);
        break;
      case 'csv-detailed':
        exportDetailedCSV(result.processedData, template, filename);
        break;
      default:
        break;
    }
  };

  // Создание новости из отчета
  const createNewsFromReport = () => {
    if (!reportResult) return;

    const { template, result } = reportResult;

    // Перенаправляем на страницу создания медиа-новостей с данными отчета
    const reportData = {
      templateId: template.id,
      templateName: template.name,
      result: result,
      generatedAt: new Date().toISOString()
    };

    // Сохраняем данные отчета для использования в медиа-админ
    localStorage.setItem('pendingReportForNews', JSON.stringify(reportData));

    // Переходим на страницу медиа-админ
    window.location.href = '/media-admin';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Заголовок */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Конструктор отчетов
              </h1>
              <p className="text-gray-600">
                Создавайте гибкие шаблоны отчетов с фильтрами, группировкой и агрегацией данных
              </p>
            </div>

            <button
              onClick={createNewTemplate}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              + Создать шаблон
            </button>
          </div>
        </div>

        {/* Загрузка данных */}
        {(loading && allData.length === 0) || templatesLoading ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center mb-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Загружаем данные...</p>
          </div>
        ) : null}

        {/* Ошибка */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Ошибка загрузки данных</h3>
                <p className="text-sm text-red-600 mt-1">{error}</p>
                <button
                  onClick={() => refreshData()}
                  className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded transition-colors"
                >
                  Попробовать снова
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Список шаблонов */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Сохраненные шаблоны</h3>
          </div>

          {savedTemplates.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Нет сохраненных шаблонов</h3>
              <p className="text-gray-500 mb-4">
                Создайте свой первый шаблон отчета для быстрого анализа данных
              </p>
              <button
                onClick={createNewTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Создать первый шаблон
              </button>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedTemplates.map((template) => (
                  <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          {template.isShared && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Общий</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                        <p className="text-xs text-gray-500">
                          Создан: {new Date(template.createdAt).toLocaleString('ru-RU')}
                          {template.createdBy && ` (${template.createdBy})`}
                        </p>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => editTemplate(template)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Редактировать
                        </button>
                        <button
                          onClick={() => deleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Удалить
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      {template.dateRange && (template.dateRange.from || template.dateRange.to) && (
                        <div className="text-xs">
                          <span className="text-gray-500">Период:</span> {template.dateRange.from || 'начало'} — {template.dateRange.to || 'сейчас'}
                        </div>
                      )}
                      {template.filters && template.filters.length > 0 && (
                        <div className="text-xs">
                          <span className="text-gray-500">Фильтров:</span> {template.filters.length}
                        </div>
                      )}
                      {template.grouping && template.grouping.length > 0 && (
                        <div className="text-xs">
                          <span className="text-gray-500">Группировок:</span> {template.grouping.length}
                        </div>
                      )}
                      {template.aggregations && template.aggregations.length > 0 && (
                        <div className="text-xs">
                          <span className="text-gray-500">Счетчиков:</span> {template.aggregations.length}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => applyTemplate(template)}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Применить шаблон
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Конструктор шаблонов */}
        {isBuilderOpen && (
          <ReportTemplateBuilder
            data={allData}
            fieldMetadata={fieldMetadata}
            template={selectedTemplate}
            refreshData={refreshData}
            onSave={saveTemplate}
            onCancel={() => {
              setIsBuilderOpen(false);
              setSelectedTemplate(null);
            }}
          />
        )}

        {/* Модальное окно результатов и экспорта */}
        {showExportModal && reportResult && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              {/* Заголовок */}
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Результаты отчета: {reportResult.template.name}
                  </h2>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Содержимое */}
              <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {reportResult.resultText}
                  </pre>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Экспорт данных</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* CSV Сводка */}
                    <button
                      onClick={() => handleExport('csv')}
                      className="flex items-center justify-center space-x-3 px-4 py-3 border-2 border-green-200 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold">CSV (Сводка)</div>
                        <div className="text-xs">Сгруппированные данные</div>
                      </div>
                    </button>

                    {/* Excel */}
                    <button
                      onClick={() => handleExport('excel')}
                      className="flex items-center justify-center space-x-3 px-4 py-3 border-2 border-blue-200 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold">Excel</div>
                        <div className="text-xs">Таблица с форматированием</div>
                      </div>
                    </button>

                    {/* CSV Детальный */}
                    <button
                      onClick={() => handleExport('csv-detailed')}
                      className="flex items-center justify-center space-x-3 px-4 py-3 border-2 border-purple-200 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold">CSV (Детальный)</div>
                        <div className="text-xs">Все записи по отдельности</div>
                      </div>
                    </button>

                    {/* JSON */}
                    <button
                      onClick={() => handleExport('json')}
                      className="flex items-center justify-center space-x-3 px-4 py-3 border-2 border-gray-200 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                      <div className="text-left">
                        <div className="font-semibold">JSON</div>
                        <div className="text-xs">Структурированные данные</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Футер */}
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-between">
                <button
                  onClick={createNewsFromReport}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                >
                  📰 Создать новость
                </button>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportTemplatesPage;
